import PropTypes from "prop-types";
import React, { useState } from "react";

/* -------------------------------------------------------------------------
 * DonutChart — multi-segment ring chart, no chart library dependency.
 * Used by SettlementCard (You Owe / You're Owed).
 * Supports segment selection with center value display and click interaction.
 * ---------------------------------------------------------------------- */

const DonutChart = ({ segments, size, strokeWidth, trackColor, onSegmentClick, selectedIndex }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, seg) => sum + Math.abs(seg.value), 0) || 1;

  const handleSegmentClick = (index) => {
    if (onSegmentClick) {
      onSegmentClick(index);
    }
  };

  const handleSegmentMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleSegmentMouseLeave = () => {
    setHoveredIndex(null);
  };

  // Determine which segment to display in center
  const displayIndex = selectedIndex !== null ? selectedIndex : hoveredIndex !== null ? hoveredIndex : null;
  const displaySegment = displayIndex !== null ? segments[displayIndex] : null;

  let cumulative = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ cursor: 'pointer' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={strokeWidth}
      />
      {segments.map((seg, index) => {
        const fraction = Math.abs(seg.value) / total;
        const length = fraction * circumference;
        const dashoffset = -cumulative * circumference;
        cumulative += fraction;
        
        const isSelected = selectedIndex === index;
        const isHovered = hoveredIndex === index;
        const isDimmed = selectedIndex !== null && !isSelected;
        
        return (
          <circle
            key={seg.label}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${length} ${circumference - length}`}
            strokeDashoffset={dashoffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{
              opacity: isDimmed ? 0.3 : isHovered || isSelected ? 1 : 0.8,
              transition: 'opacity 0.2s ease',
              cursor: 'pointer',
            }}
            onClick={() => handleSegmentClick(index)}
            onMouseEnter={() => handleSegmentMouseEnter(index)}
            onMouseLeave={handleSegmentMouseLeave}
          />
        );
      })}
      
      {/* Center text display */}
      {displaySegment && (
        <g>
          <text
            x={size / 2}
            y={size / 2 - 8}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: '12px',
              fill: '#666',
              fontWeight: '500',
            }}
          >
            {displaySegment.label}
          </text>
          <text
            x={size / 2}
            y={size / 2 + 10}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: '18px',
              fill: displaySegment.color,
              fontWeight: '700',
            }}
          >
            {displaySegment.value >= 0 ? '+' : '-'}${Math.abs(displaySegment.value).toFixed(2)}
          </text>
        </g>
      )}
    </svg>
  );
};

DonutChart.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  trackColor: PropTypes.string,
  onSegmentClick: PropTypes.func,
  selectedIndex: PropTypes.number,
};

DonutChart.defaultProps = {
  size: 160,
  strokeWidth: 22,
  trackColor: "var(--bg-light-blue)",
};

export default DonutChart;
